import { ROLE } from './enum';
import { getClientId, getRoleFromLocalStorage } from './helper';
import {
  getResolvedRole,
  hasPermission,
  isOnCustomRole,
  isPermissionRegistryReady,
} from './permissionRegistry';

/**
 * Compatibility shim with ~98 call sites, so the helpers resolve through
 * permissions and the call sites stay untouched.
 *
 *   CAPABILITY — maps onto one permission; a custom role holding it satisfies it.
 *   IDENTITY   — "is this user literally role X?". Always false for custom roles.
 *
 * Both fall back to the legacy role comparison until /roles/me resolves.
 */

export const checkClient = (): string | boolean => {
  return getClientId() ?? false;
};

/** Server-resolved role, falling back to localStorage before /roles/me lands. */
export const currentRole = (): string | undefined => {
  const resolved = getResolvedRole();
  if (resolved) return resolved.toUpperCase();
  return getRoleFromLocalStorage()?.toUpperCase();
};

/**
 * Always false on a custom role: a custom role stores USER as its legacy mirror
 * and must not be mistaken for the built-in User.
 */
const checkRole = (role: ROLE) => {
  if (isPermissionRegistryReady() && isOnCustomRole()) return false;
  return currentRole() === role;
};

// --------------------------------------------------------------- CAPABILITY

/**
 * The legacy fallback is not optional: these run on first render, before any
 * permission fetch can complete.
 */
const capability = (permission: string, legacy: () => boolean): boolean =>
  capabilities([permission], legacy);

/** As `capability`, for a check that needs more than one permission. */
const capabilities = (permissions: string[], legacy: () => boolean): boolean => {
  if (!isPermissionRegistryReady()) return legacy();
  // Custom roles by permission; built-in roles keep the role comparison.
  return isOnCustomRole() ? permissions.every(hasPermission) : legacy();
};

/** Platform administrator. */
export const checkSuperOwner = (): boolean =>
  capability('platform:manage', () => checkRole(ROLE.SUPEROWNER));

/** Can administer roles for their organisation — Owner and above. */
export const checkOwner = (): boolean =>
  capability('roles:manage', () => checkRole(ROLE.OWNER));

/** The built-in roles that have always reached the administrative pages. */
const legacyAdmin = (): boolean => {
  const role = currentRole();
  return (
    role === ROLE.ADMIN ||
    role === ROLE.SUPEROWNER ||
    role === ROLE.OWNER ||
    role === ROLE.BEACON ||
    role === ROLE.USERKEEPME ||
    role === ROLE.KEEPME
  );
};

/** "An administrator at all?" To guard a page use `canReachAdminPage`. */
export const checkAdmin = (): boolean => capabilities(['team:read'], legacyAdmin);

/**
 * Guards one administrative page. Built-in roles keep their role comparison; a
 * custom role is measured against what that page needs. Every admin page used
 * to share `team:read`, so one tick on "View team members" opened all of them.
 */
export const canReachAdminPage = (...permissions: string[]): boolean =>
  capabilities(permissions, legacyAdmin);

/**
 * Guards one Insights page — Overview, Improve, Performance, Learnings and
 * Benchmark.
 *
 * Insights is not an administrative section, so `legacyAdmin()` is the wrong
 * legacy test for it: it omits the built-in User, who is granted
 * insights:read and iframe:read like every other built-in role (see
 * Backend/src/constant/systemRoles.ts). Without this, the sidebar offers
 * Overview to a User — it is gated on the permission — and the route then
 * bounces them back to the client index.
 *
 * Custom roles are untouched: they are still measured against the page's own
 * permission, exactly as `canReachAdminPage` measures them.
 */
export const canReachInsightsPage = (...permissions: string[]): boolean =>
  capabilities(permissions, () => legacyAdmin() || checkRole(ROLE.USER));

// ----------------------------------------------------------------- IDENTITY

/** The Keepme read-only roles. */
export const checkKeepme = (): boolean => checkRole(ROLE.KEEPME);
export const checkUserKeepme = (): boolean => checkRole(ROLE.USERKEEPME);

/** Beacon-only user. Identity, not capability — gates a whole product area. */
export const checkBeacon = (): boolean => checkRole(ROLE.BEACON);

export const checkUserAdmin = (): boolean => checkRole(ROLE.ADMIN);
export const checkUser = (): boolean => checkRole(ROLE.USER);

export const checkClientView = (): boolean => {
  const role = getRoleFromLocalStorage();
  return role === ROLE.ADMIN || role === ROLE.USER || role === ROLE.USERKEEPME;
};
/**
 * Read-only gating lives in sideMenuSlice.ts and AccountPanel, computed locally
 * as `isKeepme || isUserKeepme`. For "can this user write here", ask
 * `usePermissions().can('<module>:manage')` at the call site rather than adding
 * a global read-only flag.
 */
